import { useEffect } from 'react';

import * as Sentry from '@sentry/react';
import BadRequestError from 'App/Errors/BadRequestError';
import {
    checkForUserLeadHash,
    getUrlParamObj,
    resolveFallbackUrl,
} from 'App/Helpers';
import { getConnectionEffectiveType } from 'App/Helpers/getConnectionEffectiveType';
import Redirect from 'App/Helpers/Redirect';
import initReactSentry from 'App/Services/ErrorTracking/initReactSentry';
import { setDeviceInfoTags } from 'App/Services/ErrorTracking/setDeviceInfoTags';

import MissingUrlRidError from '../../../Errors/MissingUrlRidError';

/**
 * One-shot bootstrap after bot detection: rid check, urlParams, Sentry tags, fetch offers.
 * Effect deps stay `[botInfo.isBot, botInfo.customIsBot]` on purpose (stable config + one-shot).
 *
 * @param {object} params
 * @param {{ isBot: boolean|null, customIsBot: string|null, failedChecks: array, visitorId: string|null }} params.botInfo
 * @param {string} params.siteUrl
 * @param {string} params.env
 * @param {boolean} params.sentryEnabled
 * @param {string} params.source
 * @param {string|number} params.campaignId
 * @param {boolean} [params.checkLeadHash]
 * @param {import('App/Services/Sharpstar/SharpstarServiceHandler').default} params.sharpstarService
 * @param {import('App/Services/CookieService').default} params.cookiesService
 * @param {string} params.fallbackUrl
 * @param {Function} params.setUrlParams
 * @param {Function} params.setLeadStatus
 * @param {Function} params.setOffers
 */
export function useGamblingBootstrap({
    botInfo,
    siteUrl,
    env,
    sentryEnabled,
    source,
    campaignId,
    checkLeadHash,
    sharpstarService,
    cookiesService,
    fallbackUrl,
    setUrlParams,
    setLeadStatus,
    setOffers,
}) {
    useEffect(() => {
        if (typeof botInfo.isBot !== 'boolean') return;

        const params = getUrlParamObj();
        const { rid, hash, refid1, refid2, refid3 } = params;
        const effectiveType = getConnectionEffectiveType();

        if (!rid) {
            Sentry.captureException(new MissingUrlRidError());

            return Redirect.redirect(siteUrl, env);
        }

        setUrlParams(params);

        initReactSentry({
            enabled: sentryEnabled,
            source: source,
            campaignId,
            location: document.location.host,
            environment: env,
            refid1: refid1,
            refid2: refid2,
            refid3: refid3,
        });

        if (sentryEnabled) {
            Sentry.setTags({
                net_type: effectiveType,
                is_bot: botInfo.isBot ? 'true' : 'false',
                ...(botInfo.customIsBot && {
                    custom_is_bot: botInfo.customIsBot,
                }),
            });
            void setDeviceInfoTags();
        }

        const redirectToFallback = () =>
            Redirect.redirect(resolveFallbackUrl(fallbackUrl, refid1), env);

        const redirectIfNoOffers = (offers) => {
            if (offers?.length) return false;
            redirectToFallback();
            return true;
        };

        const fetchOffers = async () => {
            try {
                if (checkLeadHash) {
                    const userHash = await checkForUserLeadHash({
                        hash,
                        cookiesService,
                        campaignId,
                    });
                    const campaignIdCookie =
                        cookiesService.getCookieByCampaign(campaignId);

                    if (
                        userHash &&
                        campaignIdCookie?.campaign_id === campaignId
                    ) {
                        const { offers } = await sharpstarService.getOffers({
                            hash: userHash,
                            isLead: 1,
                            isBot: botInfo.isBot,
                            customIsBot: botInfo.customIsBot,
                            botFailedChecks: botInfo.failedChecks,
                            visitorId: botInfo.visitorId,
                        });

                        if (redirectIfNoOffers(offers)) return;

                        return Redirect.redirect(offers[0].link, env);
                    }
                }

                const { lead, offers } = await sharpstarService.getOffers({
                    isLead: 0,
                    hash: undefined,
                    rid,
                    isBot: botInfo.isBot,
                    customIsBot: botInfo.customIsBot,
                    botFailedChecks: botInfo.failedChecks,
                    visitorId: botInfo.visitorId,
                });

                if (redirectIfNoOffers(offers)) return;

                if (lead) setLeadStatus(lead);

                setOffers(offers);
            } catch (error) {
                if (error.name === 'BadRequestError') {
                    Sentry.captureException(new BadRequestError());
                } else if (error.name === 'NonJsonResponseError') {
                    Sentry.captureException(error, {
                        tags: { non_json_response: error.requestLabel },
                    });
                } else {
                    Sentry.captureException(error);
                }
                redirectToFallback();
            }
        };

        void fetchOffers();
    }, [botInfo.isBot, botInfo.customIsBot]);
}
